<template>
  <div class="w-100" :class="{ 'chart-empty-state': totalNumber == null || passedNumber == null || totalNumber <= 0 }">
    <svg ref="arcChart" :width="width" :height="height"></svg>
    <div class="chart-legend" v-if="legendData.length">
      <div v-for="item in legendData" :key="item.label" class="chart-legend-item">
        <span class="chart-legend-swatch" :style="{ backgroundColor: item.color }"></span>
        <span>{{ item.label }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3';
import chartEmptyStateMixin from "./chartEmptyStateMixin";

export default {
  name: 'ArcTweenChart',
  mixins: [chartEmptyStateMixin],
  props: {
    totalNumber: {
      type: Number,
      required: true,
      default: 100,
    },
    passedNumber: {
      type: Number,
      required: true,
      default: 50,
    },
    color1: {
      type: String,
      default: '#1AB394',
    },
    color2: {
      type: String,
      default: '#f44336', 
    },
    labelOne:{
      type: String,
      default: 'Acceptable', 
    },
    labelTwo:{
      type: String,
      default: 'Rejected', 
    },
    width: {
      type: Number,
      default: 100,
    },
    height: {
      type: Number,
      default: 100,
    },
  },
  watch: {
    totalNumber() {
      this.createChart();
    },
    passedNumber() {
      this.createChart();
    },
  },
  data() {
    return {
      legendData: [
        { label: this.labelOne, color: this.color1 },
        { label: this.labelTwo, color: this.color2 },
      ],
    };
  },
  mounted() {
    this.createChart();
  },
  methods: {
    createChart() {
      if (this.totalNumber == null || this.passedNumber == null || this.totalNumber <= 0) {
        d3.select(this.$refs.arcChart).selectAll('*').remove();
        return;
      }

      const width = this.width;
      const height = this.height;
      const radius = Math.min(width, height) / 2;

      const svg = d3.select(this.$refs.arcChart)
        .attr('width', width + "%")
        .attr('height', height)
        .attr('viewBox', [0, 0, width, height]);

      svg.selectAll('*').remove(); 

      const arc = d3.arc()
        .innerRadius(radius * 0.7)
        .outerRadius(radius);

      const pie = d3.pie()
        .sort(null)
        .value(d => d.value);

      const acceptablePercentage = (this.passedNumber / this.totalNumber) * 100;
      const rejectablePercentage = 100 - acceptablePercentage;

      const data = [
        { name: this.labelOne, value: this.passedNumber },
        { name: this.labelTwo, value: this.totalNumber - this.passedNumber },
      ];

      const arcs = pie(data);

      const color = d3.scaleOrdinal()
        .domain(data.map(d => d.name))
        .range([this.color1, this.color2]);

      const g = svg.append('g')
        .attr('transform', `translate(${width / 2}, ${height / 2})`);

      g.selectAll('path')
        .data(arcs)
        .enter()
        .append('path')
        .attr('fill', d => color(d.data.name))
        .transition()
        .duration(1000)
        .attrTween('d', function (d) {
          const i = d3.interpolate(d.startAngle, d.endAngle);
          return function (t) {
            d.endAngle = i(t);
            return arc(d);
          };
        });

     
      svg.append('text')
        .attr('text-anchor', 'middle')
        .attr('x', -3)
        .attr('y', height / 2 - radius / 2 - 10)
        .style('font-size', '12px')
        .style('fill', '#000')
        .text(`${rejectablePercentage.toFixed(2)}%`);
        

      
      svg.append('text')
        .attr('text-anchor', 'middle')
        .attr('x', 110)
        .attr('y', height / 2 - radius / 2 - 10)
        .style('font-size', '12px')
        .style('fill', '#000')
        .text(`${acceptablePercentage.toFixed(2)}%`);

      svg.append('text')
        .attr('text-anchor', 'middle')
        .attr('x', width / 2)
        .attr('y', height / 2 + 10)
        .style('font-size', '14px')
        .style('fill', '#000')
        .text(`${this.passedNumber}/${this.totalNumber}`);
    },
  },
};
</script>

<style scoped>
@import "./charts.css";

div {
  text-align: center;
}
.chart-legend {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px 16px;
  margin-top: 10px;
}
.chart-legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #333;
}
.chart-legend-swatch {
  width: 12px;
  height: 12px;
  border-radius: 2px;
  border: 1px solid rgba(0, 0, 0, 0.15);
}
</style>
